import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Button from '@tds/core-button';
import Switch from 'react-switch';
import { isEqual, isAfter } from 'date-fns';

import request from '../../../../../../../../../utils/request';
import {
  Spacer,
  ErrorMessage,
  BlockBodyColumn,
  BlockBodyRow,
  BlockPermissionName,
  BlockPermissionDescription
} from '../../../css';

const PermissionItem = ({
  permission,
  orderPermissions,
  onUpdatePermission,
  setUpdating
}) => {
  const { id, name: permissionName, description } = permission;
  const [dateRange, setDateRange] = useState([null, null]);
  const [errorMessage, setErrorMessage] = useState('');
  const [startDate, endDate] = dateRange;

  const permissionData = useMemo(() => {
    return orderPermissions.find(orderPermission => orderPermission.id === id);
  }, [orderPermissions]);

  const checkPermissionEnabled = permissionData
    ? permissionData.isEnabled
    : false;

  const areDatesSame =
    permissionData &&
    (!startDate && !permissionData.startDate
      ? true
      : isEqual(startDate, new Date(permissionData.startDate)) &&
        isEqual(endDate, new Date(permissionData.endDate)));

  const areDatesAdded =
    permissionData && permissionData.startDate && permissionData.endDate;

  const updatePermission = async (
    permissionId,
    permissionStartDate,
    permissionEndDate,
    isEnabled
  ) => {
    try {
      setUpdating(true);
      if (!isEnabled) {
        setDateRange([null, null]);
      }
      const result = await request.post(`/order-permissions-dates`, {
        id: permissionId,
        startDate: isEnabled ? permissionStartDate : null,
        endDate: isEnabled ? permissionEndDate : null,
        isEnabled
      });

      if (result && result.status === 200) {
        setUpdating(false);
        onUpdatePermission();
      }
    } catch (err) {
      setUpdating(false);
    }
  };

  const onPermissionChange = (permissionId, checked) => {
    updatePermission(permissionId, '', '', checked);
  };

  useEffect(() => {
    if (permissionData && permissionData.startDate && permissionData.endDate) {
      if (isAfter(new Date(), new Date(permissionData.endDate))) {
        onPermissionChange(id, false);
      } else {
        setDateRange([
          new Date(permissionData.startDate),
          new Date(permissionData.endDate)
        ]);
      }
    }
  }, [permissionData]);

  const onRemoveSchedule = () => {
    updatePermission(id, null, null, permissionData.isEnabled);
    setDateRange([null, null]);
  };

  const onUpdateSchedule = () => {
    if (startDate && endDate) {
      if (startDate.getTime() === endDate.getTime()) {
        setErrorMessage('Please select different start and end dates');
        return;
      }
      updatePermission(id, startDate, endDate, permissionData.isEnabled);
      setErrorMessage('');
    } else {
      setErrorMessage('Date range not added correctly');
    }
  };

  return (
    <>
      <BlockBodyColumn key={id}>
        <BlockBodyRow>
          <BlockPermissionName>{permissionName}</BlockPermissionName>
          <Switch
            checked={checkPermissionEnabled}
            onChange={checked => onPermissionChange(id, checked)}
          />
        </BlockBodyRow>
        <BlockPermissionDescription>{description}</BlockPermissionDescription>
        <Spacer height={10} />
        {checkPermissionEnabled && (
          <>
            <DatePicker
              placeholderText="Schedule not added"
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={update => {
                setDateRange(update);
              }}
              withPortal
              minDate={new Date()}
            />
            <Spacer height={6} />
            {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            {!areDatesSame && (
              <>
                <Button type="button" onClick={onUpdateSchedule}>
                  Update
                </Button>
                <Spacer height={6} />
              </>
            )}
            {areDatesAdded && (
              <Button
                variant="secondary"
                type="button"
                onClick={onRemoveSchedule}
              >
                Remove Schedule
              </Button>
            )}
          </>
        )}
      </BlockBodyColumn>
      <Spacer height={4} />
    </>
  );
};

PermissionItem.propTypes = {
  permission: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string
  }).isRequired,
  orderPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      isEnabled: PropTypes.bool,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string
    })
  ).isRequired,
  onUpdatePermission: PropTypes.func.isRequired,
  setUpdating: PropTypes.func.isRequired
};

export default PermissionItem;
